/**
 * Team Management Component
 * Manage team members: invite, change roles, activate/deactivate
 */

import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  Typography,
  message,
  Popconfirm,
  Badge,
  Tooltip,
  Row,
  Col,
  Divider,
} from 'antd';
import {
  PlusOutlined,
  UserSwitchOutlined,
  StopOutlined,
  CheckCircleOutlined,
  TeamOutlined,
  MailOutlined,
  LockOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { teamApi } from '../../ai-crm/api/teamApi';
import type { TeamMember, CreateMemberInput } from '../../ai-crm/api/teamApi';
import { usePermissions } from '../../../hooks/usePermissions';
import RolePermissions from './RolePermissions';

const { Text } = Typography;

const ROLE_COLORS: Record<string, string> = {
  OWNER: 'red',
  ADMIN: 'volcano',
  MANAGER: 'orange',
  SALES_REP: 'blue',
  VIEWER: 'default',
};

const TeamManagement: React.FC = () => {
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission('team:manage');
  const canManageRoles = hasPermission('team:manage_roles');

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [selectedRoleCode, setSelectedRoleCode] = useState<string>('');
  const [form] = Form.useForm();

  // ---- Data fetching ----

  const {
    data: members = [],
    isLoading: membersLoading,
  } = useQuery({
    queryKey: ['teamMembers'],
    queryFn: () => teamApi.getMembers(),
  });

  const {
    data: roles = [],
    isLoading: rolesLoading,
  } = useQuery({
    queryKey: ['teamRoles'],
    queryFn: () => teamApi.getRoles(),
  });

  // ---- Mutations ----

  const createMutation = useMutation({
    mutationFn: (data: CreateMemberInput) => teamApi.createMember(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teamMembers'] });
      queryClient.invalidateQueries({ queryKey: ['teamRoles'] });
      message.success('Team member added successfully');
      setAddModalOpen(false);
      form.resetFields();
    },
    onError: (err: any) => {
      message.error(err?.response?.data?.message || 'Failed to add team member');
    },
  });

  const changeRoleMutation = useMutation({
    mutationFn: ({ userId, roleCode }: { userId: string; roleCode: string }) =>
      teamApi.changeRole(userId, roleCode),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teamMembers'] });
      queryClient.invalidateQueries({ queryKey: ['teamRoles'] });
      message.success('Role updated successfully');
      setRoleModalOpen(false);
      setSelectedMember(null);
    },
    onError: (err: any) => {
      message.error(err?.response?.data?.message || 'Failed to change role');
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: (userId: string) => teamApi.deactivateMember(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teamMembers'] });
      message.success('Member deactivated');
    },
    onError: (err: any) => {
      message.error(err?.response?.data?.message || 'Failed to deactivate member');
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: (userId: string) => teamApi.reactivateMember(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teamMembers'] });
      message.success('Member reactivated');
    },
    onError: (err: any) => {
      message.error(err?.response?.data?.message || 'Failed to reactivate member');
    },
  });

  // ---- Handlers ----

  const handleAddMember = async () => {
    try {
      const values = await form.validateFields();
      createMutation.mutate(values);
    } catch {
      // validation failed
    }
  };

  const openChangeRole = (member: TeamMember) => {
    setSelectedMember(member);
    setSelectedRoleCode(member.companyRoleCode);
    setRoleModalOpen(true);
  };

  const handleChangeRole = () => {
    if (!selectedMember) return;
    changeRoleMutation.mutate({
      userId: selectedMember.id,
      roleCode: selectedRoleCode,
    });
  };

  // ---- Table columns ----

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: TeamMember) => (
        <Space>
          <UserOutlined />
          <div>
            <Text strong>{name}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.email}
            </Text>
          </div>
        </Space>
      ),
      responsive: ['xs' as const, 'sm' as const, 'md' as const, 'lg' as const, 'xl' as const],
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      responsive: ['md' as const, 'lg' as const, 'xl' as const],
    },
    {
      title: 'Role',
      key: 'role',
      dataIndex: 'companyRoleName',
      render: (roleName: string, record: TeamMember) => (
        <Tag color={ROLE_COLORS[record.companyRoleCode] || 'default'}>
          {roleName || record.companyRoleCode}
        </Tag>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_: any, record: TeamMember) =>
        record.isActive ? (
          <Badge status="success" text="Active" />
        ) : (
          <Badge status="error" text="Inactive" />
        ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: TeamMember) => {
        // Don't allow managing the OWNER account
        if (record.companyRoleCode === 'OWNER') {
          return <Text type="secondary">--</Text>;
        }

        if (!canManage) return null;

        return (
          <Space size="small" wrap>
            <Tooltip title="Change Role">
              <Button
                size="small"
                icon={<UserSwitchOutlined />}
                onClick={() => openChangeRole(record)}
              />
            </Tooltip>
            {record.isActive ? (
              <Popconfirm
                title="Deactivate this member?"
                description="They will no longer be able to log in."
                onConfirm={() => deactivateMutation.mutate(record.id)}
                okText="Yes"
                cancelText="No"
              >
                <Tooltip title="Deactivate">
                  <Button size="small" danger icon={<StopOutlined />} />
                </Tooltip>
              </Popconfirm>
            ) : (
              <Popconfirm
                title="Reactivate this member?"
                description="They will be able to log in again."
                onConfirm={() => reactivateMutation.mutate(record.id)}
                okText="Yes"
                cancelText="No"
              >
                <Tooltip title="Reactivate">
                  <Button size="small" icon={<CheckCircleOutlined />} style={{ color: '#52c41a' }} />
                </Tooltip>
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      {/* Members Section */}
      <Card
        title={
          <Space>
            <TeamOutlined />
            <span>Team Members</span>
          </Space>
        }
        extra={
          canManage ? (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                form.resetFields();
                setAddModalOpen(true);
              }}
            >
              Add Team Member
            </Button>
          ) : null
        }
        style={{ marginBottom: 24 }}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          Manage your team members and their roles. Each member can be assigned a role that controls their access permissions.
        </Text>

        <Table
          dataSource={members}
          columns={columns}
          rowKey="id"
          loading={membersLoading}
          pagination={members.length > 10 ? { pageSize: 10 } : false}
          size="middle"
          scroll={{ x: 600 }}
        />
      </Card>

      {/* Role Permissions Section - hidden for now, too complex for initial release */}
      {/* {canManageRoles && (
        <>
          <Divider />
          <RolePermissions />
        </>
      )} */}

      {/* Add Member Modal */}
      <Modal
        title="Add Team Member"
        open={addModalOpen}
        onCancel={() => {
          setAddModalOpen(false);
          form.resetFields();
        }}
        onOk={handleAddMember}
        confirmLoading={createMutation.isPending}
        width={480}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="name"
            label="Full Name"
            rules={[{ required: true, message: 'Please enter the member name' }]}
          >
            <Input prefix={<UserOutlined />} placeholder="e.g. John Smith" />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email Address"
            rules={[
              { required: true, message: 'Please enter an email' },
              { type: 'email', message: 'Please enter a valid email' },
            ]}
          >
            <Input prefix={<MailOutlined />} placeholder="e.g. john@company.com" />
          </Form.Item>

          <Form.Item
            name="password"
            label="Password"
            rules={[
              { required: true, message: 'Please enter a password' },
              { min: 6, message: 'Password must be at least 6 characters' },
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="Minimum 6 characters" />
          </Form.Item>

          <Form.Item
            name="roleCode"
            label="Role"
            rules={[{ required: true, message: 'Please select a role' }]}
          >
            <Select
              placeholder="Select a role"
              loading={rolesLoading}
              options={roles
                .filter((r) => r.code !== 'OWNER')
                .map((r) => ({
                  label: (
                    <Space>
                      <Tag color={ROLE_COLORS[r.code] || 'default'}>{r.name}</Tag>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {r.description}
                      </Text>
                    </Space>
                  ),
                  value: r.code,
                }))}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Change Role Modal */}
      <Modal
        title={`Change Role for ${selectedMember?.name || ''}`}
        open={roleModalOpen}
        onCancel={() => {
          setRoleModalOpen(false);
          setSelectedMember(null);
        }}
        onOk={handleChangeRole}
        confirmLoading={changeRoleMutation.isPending}
        width={420}
      >
        <div style={{ marginTop: 16 }}>
          <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            Current role:{' '}
            <Tag color={ROLE_COLORS[selectedMember?.companyRoleCode || ''] || 'default'}>
              {selectedMember?.companyRoleName || selectedMember?.companyRoleCode}
            </Tag>
          </Text>

          <Text strong style={{ display: 'block', marginBottom: 8 }}>
            New Role:
          </Text>
          <Select
            style={{ width: '100%' }}
            value={selectedRoleCode}
            onChange={setSelectedRoleCode}
            loading={rolesLoading}
            options={roles
              .filter((r) => r.code !== 'OWNER')
              .map((r) => ({
                label: (
                  <Row justify="space-between" align="middle">
                    <Col>
                      <Tag color={ROLE_COLORS[r.code] || 'default'}>{r.name}</Tag>
                    </Col>
                    <Col>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {r.memberCount} member{r.memberCount !== 1 ? 's' : ''}
                      </Text>
                    </Col>
                  </Row>
                ),
                value: r.code,
              }))}
          />
        </div>
      </Modal>
    </div>
  );
};

export default TeamManagement;
